import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Music, Check, ChevronLeft, ChevronRight, Building2, PartyPopper } from "lucide-react";
import axios from "axios";
import { API_BASE as API } from "../lib/api";
const CHOIR_TYPES = ["Cathedral Choir", "Church Choir", "School Choir", "Chapel Choir", "Youth Choir", "Other Choir"];
const CHANT_DEFAULT = ["Cathedral Choir", "Church Choir", "Chapel Choir"];
const STEP_TITLES = ["Your account", "Your choir", "Institution code", "Learning modules", "First group", "How to begin", "Confirm"];

export default function SignupWizard() {
  const [step, setStep] = useState(0);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({
    admin_name: "", email: "", password: "", confirm: "",
    org_name: "", choir_type: "", code: "", module_ids: [],
    group_name: "", explore: "real", terms: false, privacy: false, dpa: false,
  });
  const [codeStatus, setCodeStatus] = useState(null); // null | "checking" | "ok" | "taken" | "invalid"
  const [codeEdited, setCodeEdited] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/public/modules`).then((r) => setModules(r.data.modules)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // auto-generate code from org name until the user edits it
  useEffect(() => {
    if (!codeEdited && form.org_name) {
      const base = form.org_name.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 10);
      setForm((f) => ({ ...f, code: base }));
    }
  }, [form.org_name, codeEdited]);

  useEffect(() => {
    if (!form.code || form.code.length < 4) { setCodeStatus(form.code ? "invalid" : null); return; }
    setCodeStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/signup/check-code`, { code: form.code });
        setCodeStatus(data.available ? "ok" : "taken");
        if (data.code) setForm((f) => ({ ...f, code: data.code }));
      } catch { setCodeStatus(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.code]);

  // apply module defaults when choir type chosen
  useEffect(() => {
    if (!form.choir_type || !modules.length) return;
    const chantOn = CHANT_DEFAULT.includes(form.choir_type);
    setForm((f) => ({
      ...f,
      module_ids: modules.filter((m) => !m.liturgical || chantOn).map((m) => m.id),
    }));
  }, [form.choir_type, modules]);

  const toggleModule = (id) =>
    setForm((f) => ({ ...f, module_ids: f.module_ids.includes(id) ? f.module_ids.filter((m) => m !== id) : [...f.module_ids, id] }));

  const stepValid = useMemo(() => {
    switch (step) {
      case 0: return form.admin_name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.password.length >= 8 && form.password === form.confirm;
      case 1: return form.org_name.trim().length >= 2 && !!form.choir_type;
      case 2: return codeStatus === "ok";
      case 3: return form.module_ids.length >= 1;
      case 4: return true;
      case 5: return !!form.explore;
      case 6: return form.explore === "demo" || (form.terms && form.privacy && form.dpa);
      default: return false;
    }
  }, [step, form, codeStatus]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/signup/complete`, {
        admin_name: form.admin_name, email: form.email, password: form.password,
        org_name: form.org_name, choir_type: form.choir_type, code: form.code,
        module_ids: form.module_ids, group_name: form.group_name || "Main Choir",
        explore: form.explore, terms_accepted: form.terms && form.privacy && form.dpa,
      });
      setDone(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B192C] text-[#FAF7EE] flex items-center justify-center px-5" data-testid="signup-success">
        <div className="w-full max-w-md text-center">
          <PartyPopper className="w-14 h-14 text-[#C69B3D] mx-auto" aria-hidden />
          <h1 className="font-display text-3xl font-bold mt-4">Welcome aboard, {done.institution.name}!</h1>
          <div className="mt-6 rounded-2xl bg-[#12253E] border border-[#1E3A5F] p-6 text-left space-y-2 text-sm">
            <p><span className="text-[#FAF7EE]/60">Institution code:</span> <span className="font-note font-bold text-[#C69B3D] text-lg" data-testid="success-code">{done.institution.code}</span></p>
            <p><span className="text-[#FAF7EE]/60">Free trial until:</span> <strong>{new Date(done.institution.trial_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</strong></p>
            <p><span className="text-[#FAF7EE]/60">Trial includes:</span> up to {done.institution.learner_limit} chorister profiles, full access to core features, no card required.</p>
            <p className="text-[#FAF7EE]/60">{done.email_sent ? "We've emailed you a confirmation link and getting-started checklist." : "Email could not be sent — you can still sign in right away."}</p>
          </div>
          <button onClick={() => navigate("/staff")} data-testid="success-signin-btn"
            className="mt-8 w-full px-6 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[52px]">
            Sign in to your staff portal
          </button>
        </div>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border-2 border-[#1E3A5F] bg-[#12253E] px-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[52px] text-[#FAF7EE]";
  const labelCls = "block text-xs font-bold uppercase tracking-widest text-[#FAF7EE]/60 mb-1.5";

  return (
    <div className="min-h-screen bg-[#0B192C] text-[#FAF7EE] flex flex-col" data-testid="signup-wizard">
      <header className="px-5 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="back-to-home">
          <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
          <span className="font-display font-bold text-lg">ChoirQuest</span>
        </Link>
        <span className="text-xs text-[#FAF7EE]/50">14-day free trial · no card required</span>
      </header>

      <main className="flex-1 w-full max-w-xl mx-auto px-5 pb-12">
        <div className="flex items-center gap-1.5 my-6" data-testid="step-indicator">
          {STEP_TITLES.map((t, i) => (
            <div key={t} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#C69B3D]" : "bg-[#1E3A5F]"}`} />
          ))}
        </div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#C69B3D]">Step {step + 1} of {STEP_TITLES.length}</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1" data-testid="step-title">{STEP_TITLES[step]}</h1>

        <div className="mt-6 space-y-4">
          {step === 0 && (
            <>
              <div><label className={labelCls} htmlFor="su-name">Your name</label>
                <input id="su-name" data-testid="su-name" className={inputCls} value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="e.g. Sarah Clarke" /></div>
              <div><label className={labelCls} htmlFor="su-email">Work email</label>
                <input id="su-email" type="email" data-testid="su-email" className={inputCls} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@yourchoir.org" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls} htmlFor="su-password">Password (8+ characters)</label>
                  <input id="su-password" type="password" data-testid="su-password" className={inputCls} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                <div><label className={labelCls} htmlFor="su-confirm">Confirm password</label>
                  <input id="su-confirm" type="password" data-testid="su-confirm" className={inputCls} value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
                  {form.confirm && form.password !== form.confirm && <p className="text-xs text-[#ff9d9d] mt-1">Passwords don't match</p>}</div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div><label className={labelCls} htmlFor="su-org">Organisation name</label>
                <input id="su-org" data-testid="su-org" className={inputCls} value={form.org_name}
                  onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="e.g. St Mary's Parish Choir" /></div>
              <div>
                <span className={labelCls}>What kind of choir are you setting up?</span>
                <div className="grid grid-cols-2 gap-2.5" role="radiogroup">
                  {CHOIR_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => setForm({ ...form, choir_type: t })} data-testid={`choir-type-${t.replace(/\s+/g, "-")}`}
                      className={`px-4 py-4 rounded-xl border-2 text-sm font-bold text-left transition-colors min-h-[52px] ${form.choir_type === t ? "border-[#C69B3D] bg-[#C69B3D]/10 text-[#C69B3D]" : "border-[#1E3A5F] text-[#FAF7EE]/80 hover:border-[#C69B3D]/50"}`}>
                      {form.choir_type === t && <Check className="w-4 h-4 inline mr-1.5" aria-hidden />}{t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-[#FAF7EE]/70">Your singers will use this code to sign in. We've suggested one — you can change it.</p>
              <div>
                <label className={labelCls} htmlFor="su-code">Institution code</label>
                <input id="su-code" data-testid="su-code" className={`${inputCls} font-note text-lg tracking-widest uppercase`}
                  value={form.code}
                  onChange={(e) => { setCodeEdited(true); setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }); }} />
                <p className="text-xs mt-2" data-testid="code-status">
                  {codeStatus === "checking" && <span className="text-[#FAF7EE]/50">Checking…</span>}
                  {codeStatus === "ok" && <span className="text-[#2D6A4F] font-bold">✓ {form.code} is available</span>}
                  {codeStatus === "taken" && <span className="text-[#ff9d9d]">That code is taken — try another</span>}
                  {codeStatus === "invalid" && <span className="text-[#ff9d9d]">Codes need 4–16 letters or numbers</span>}
                </p>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-[#FAF7EE]/70">
                {CHANT_DEFAULT.includes(form.choir_type)
                  ? "Suggested for your choir type — everything is editable, now and later."
                  : "Suggested for your choir type — Psalms & Anglican Chant is off by default; enable it any time."}
              </p>
              <div className="space-y-2.5">
                {modules.map((m) => (
                  <label key={m.id} className={`flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors min-h-[52px] ${form.module_ids.includes(m.id) ? "border-[#C69B3D] bg-[#C69B3D]/10" : "border-[#1E3A5F]"}`}
                    data-testid={`module-choice-${m.key}`}>
                    <input type="checkbox" checked={form.module_ids.includes(m.id)} onChange={() => toggleModule(m.id)}
                      className="w-5 h-5 mt-0.5 accent-[#C69B3D]" />
                    <span>
                      <span className="font-bold text-sm block">{m.name}{m.liturgical && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#C69B3D]">Optional · Liturgical</span>}</span>
                      <span className="text-xs text-[#FAF7EE]/60">{m.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="text-sm text-[#FAF7EE]/70">Groups let you assign Quests and modules to sections of your choir (e.g. Probationers, Senior Choristers). You can add more later.</p>
              <div><label className={labelCls} htmlFor="su-group">First group name</label>
                <input id="su-group" data-testid="su-group" className={inputCls} value={form.group_name}
                  onChange={(e) => setForm({ ...form, group_name: e.target.value })} placeholder="Main Choir" /></div>
            </>
          )}

          {step === 5 && (
            <div className="grid gap-3">
              {[
                { v: "demo", title: "Explore with fictional choristers first", body: "We'll add four made-up singers to your group so you can see how everything works before adding real children." },
                { v: "real", title: "Add my own choristers now", body: "Go straight to adding real choristers. You'll confirm the institution terms and privacy information first." },
              ].map((o) => (
                <button key={o.v} type="button" onClick={() => setForm({ ...form, explore: o.v })} data-testid={`explore-${o.v}`}
                  className={`text-left rounded-xl border-2 p-5 transition-colors ${form.explore === o.v ? "border-[#C69B3D] bg-[#C69B3D]/10" : "border-[#1E3A5F] hover:border-[#C69B3D]/50"}`}>
                  <span className="font-bold block">{o.title}</span>
                  <span className="text-sm text-[#FAF7EE]/60 mt-1 block">{o.body}</span>
                </button>
              ))}
            </div>
          )}

          {step === 6 && (
            <>
              <div className="rounded-xl bg-[#12253E] border border-[#1E3A5F] p-5 text-sm space-y-1.5" data-testid="signup-summary">
                <p><span className="text-[#FAF7EE]/50">Organisation:</span> <strong>{form.org_name}</strong> ({form.choir_type})</p>
                <p><span className="text-[#FAF7EE]/50">Code:</span> <span className="font-note font-bold text-[#C69B3D]">{form.code}</span></p>
                <p><span className="text-[#FAF7EE]/50">Admin:</span> {form.admin_name} · {form.email}</p>
                <p><span className="text-[#FAF7EE]/50">Modules:</span> {form.module_ids.length} of {modules.length} enabled</p>
                <p><span className="text-[#FAF7EE]/50">Trial:</span> 14 days free · up to 15 choristers · no card required</p>
              </div>
              {form.explore === "real" && (
                <div className="space-y-2.5" data-testid="terms-block">
                  {[
                    ["terms", "I accept the institution terms of use [placeholder — formal legal text to be confirmed]"],
                    ["privacy", "I have read the privacy information and confirm I have authority to add child profiles"],
                    ["dpa", "I accept the data-processing information (ChoirQuest acts as data processor for my institution)"],
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-start gap-3 rounded-xl border border-[#1E3A5F] p-4 cursor-pointer min-h-[52px]">
                      <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                        data-testid={`consent-${k}`} className="w-5 h-5 mt-0.5 accent-[#C69B3D]" />
                      <span className="text-sm text-[#FAF7EE]/80">{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {error && <p role="alert" className="mt-4 text-sm text-[#ff9d9d]" data-testid="signup-error">{error}</p>}

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} data-testid="wizard-back"
              className="inline-flex items-center gap-1.5 px-5 py-4 rounded-2xl border-2 border-[#1E3A5F] font-bold min-h-[52px]">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}
          {step < STEP_TITLES.length - 1 ? (
            <button onClick={() => stepValid && setStep(step + 1)} disabled={!stepValid} data-testid="wizard-next"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors disabled:opacity-40 min-h-[52px]">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={!stepValid || busy} data-testid="wizard-create"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors disabled:opacity-40 min-h-[52px]">
              {busy ? "Setting up…" : "Create my choir"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
